/**
 * PE Collateral Assessment Page
 *
 * Two-step workflow:
 *   Step 1 -- Submit fund and collateral parameters, receive assessment with
 *             risk rating, CHA tier thresholds, and liquidation parameters.
 *   Step 2 -- Run stress tests against predefined decline scenarios.
 *
 * Calls the PE Valuation API at /api/v1/pe/valuation/collateral-assessment
 * and /api/v1/pe/valuation/stress-test.
 */
import { useState } from 'react';
import {
  Card,
  Heading,
  BodyText,
  FormLabel,
  Input,
  Button,
  Select,
  Badge,
  MetricDisplay,
  FormHelperText,
  type SelectOption,
  TableScroll,
} from '@aaimplatform/ui';
import { Switch } from '@aaimplatform/ui';
import { createPeValuationClient } from '@aaimplatform/institutional-types/api-clients';
import { csrfMiddleware } from '../api/client';

const peValuationClient = createPeValuationClient();
// CSRF double-submit echo (N8) on mutating PE-valuation requests.
peValuationClient.use(csrfMiddleware);

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

const FUND_STRATEGY_OPTIONS: SelectOption[] = [
  { value: 'large_cap_buyout', label: 'Large Cap Buyout' },
  { value: 'mid_market_buyout', label: 'Mid Market Buyout' },
  { value: 'small_cap_buyout', label: 'Small Cap Buyout' },
  { value: 'growth_equity', label: 'Growth Equity' },
  { value: 'venture_capital', label: 'Venture Capital' },
  { value: 'permanent_capital', label: 'Permanent Capital' },
  { value: 'co_investment', label: 'Co-Investment' },
  { value: 'secondary', label: 'Secondary' },
];

const GP_TIER_OPTIONS: SelectOption[] = [
  { value: 'top_quartile', label: 'Top Quartile' },
  { value: 'second_quartile', label: 'Second Quartile' },
  { value: 'third_quartile', label: 'Third Quartile' },
  { value: 'bottom_quartile', label: 'Bottom Quartile' },
];

const DISTRIBUTION_FREQUENCY_OPTIONS: SelectOption[] = [
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi_annual', label: 'Semi-Annual' },
  { value: 'annual', label: 'Annual' },
  { value: 'at_realization', label: 'At Realization' },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import type { components } from '@aaimplatform/institutional-types/pe-valuation';

type CollateralAssessmentResponse = components['schemas']['CollateralAssessmentResponse'];
type StressScenarioResult = components['schemas']['StressScenarioResult'];

// Internal: not an API contract
interface FormState {
  fundId: string;
  commitmentAmount: string;
  calledCapitalPercent: string;
  currentFundNav: string;
  fundStrategy: string;
  gpTier: string;
  gpConsentRequired: boolean;
  rofrApplies: boolean;
  pledgePermitted: boolean;
  distributionFrequency: string;
}

const INITIAL_FORM: FormState = {
  fundId: '',
  commitmentAmount: '',
  calledCapitalPercent: '',
  currentFundNav: '',
  fundStrategy: '',
  gpTier: '',
  gpConsentRequired: true,
  rofrApplies: false,
  pledgePermitted: true,
  distributionFrequency: 'quarterly',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPercent(decimal: number): string {
  return `${(decimal * 100).toFixed(1)}%`;
}

const RISK_RATING_STYLES: Record<string, string> = {
  low: 'bg-growth/10 text-growth border-growth/30',
  moderate: 'bg-warning/10 text-warning border-warning/30',
  elevated: 'bg-warning/10 text-warning border-warning/30',
  high: 'bg-error/10 text-error border-error/30',
};

const SEVERITY_BADGE_VARIANT: Record<string, 'neutral' | 'info' | 'warning' | 'error'> = {
  low: 'neutral',
  moderate: 'info',
  elevated: 'warning',
  high: 'error',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PeCollateralAssessmentPage() {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [assessment, setAssessment] = useState<CollateralAssessmentResponse | null>(null);
  const [stressResults, setStressResults] = useState<StressScenarioResult[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStressLoading, setIsStressLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stressError, setStressError] = useState<string | null>(null);

  const updateField = (field: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid =
    form.fundId.trim() !== '' &&
    form.commitmentAmount !== '' &&
    form.calledCapitalPercent !== '' &&
    form.currentFundNav !== '' &&
    form.fundStrategy !== '' &&
    form.gpTier !== '' &&
    form.distributionFrequency !== '';

  // Step 1 -- Collateral Assessment
  const handleAssessment = async () => {
    if (!isFormValid) return;
    setIsLoading(true);
    setError(null);
    setAssessment(null);
    setStressResults(null);

    try {
      type FundStrategy = NonNullable<components['schemas']['LpInterestValuationRequest']['fundStrategy']>;
      type GpTier = NonNullable<components['schemas']['LpInterestValuationRequest']['gpTier']>;
      type DistFreq = NonNullable<components['schemas']['FundTermsForAssessment']['distributionFrequency']>;

      const { data, error: apiError } = await peValuationClient.POST(
        '/api/v1/pe/valuation/collateral-assessment',
        {
          body: {
            interestValuation: {
              fundId: form.fundId.trim(),
              commitmentAmount: parseFloat(form.commitmentAmount),
              calledCapitalPercent: parseFloat(form.calledCapitalPercent),
              currentFundNav: parseFloat(form.currentFundNav),
              fundStrategy: form.fundStrategy as FundStrategy,
              gpTier: form.gpTier as GpTier,
              includeUnfundedLiability: true,
            },
            fundTerms: {
              gpConsentRequired: form.gpConsentRequired,
              gpConsentTimelineDays: 30,
              rofrApplies: form.rofrApplies,
              rofrExercisePeriodDays: 30,
              pledgePermitted: form.pledgePermitted,
              distributionFrequency: form.distributionFrequency as DistFreq,
            },
          },
        }
      );
      if (apiError || !data) {
        const msg = (apiError as { message?: string })?.message
          ?? 'Failed to retrieve collateral assessment. Verify the PE Valuation service is available.';
        throw new Error(msg);
      }
      setAssessment(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retrieve collateral assessment.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2 -- Stress Test
  const handleStressTest = async () => {
    if (!assessment) return;
    setIsStressLoading(true);
    setStressError(null);

    try {
      const { data, error: apiError } = await peValuationClient.POST(
        '/api/v1/pe/valuation/stress-test',
        {
          body: {
            interestId: form.fundId.trim(),
            scenarios: [
              { name: 'Moderate Decline', navDeclinePercent: 0.25 },
              { name: 'Severe Decline', navDeclinePercent: 0.45 },
              { name: 'Extreme Decline', navDeclinePercent: 0.60 },
            ],
          },
        }
      );
      if (apiError || !data) {
        const msg = (apiError as { message?: string })?.message
          ?? 'Failed to execute stress test. Verify the PE Valuation service is available.';
        throw new Error(msg);
      }
      setStressResults(data.scenarioResults);
    } catch (err) {
      setStressError(err instanceof Error ? err.message : 'Failed to execute stress test.');
    } finally {
      setIsStressLoading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <BodyText className="text-secondary mt-2">
            Evaluate private equity fund interests as loan collateral with risk analysis
            and stress testing
          </BodyText>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Step 1: Assessment Form                                             */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card>
          <Heading level={3} as={3} size="card" className="mb-4">Fund &amp; Collateral Parameters</Heading>
          <div className="space-y-4">
            <div>
              <FormLabel htmlFor="ca-fundId">Fund ID</FormLabel>
              <Input
                id="ca-fundId"
                type="text"
                value={form.fundId}
                onChange={(e) => updateField('fundId', e.target.value)}
                placeholder="Enter fund identifier"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FormLabel htmlFor="ca-commitment">Commitment Amount</FormLabel>
                <Input
                  id="ca-commitment"
                  type="number"
                  min={0}
                  step={100000}
                  value={form.commitmentAmount}
                  onChange={(e) => updateField('commitmentAmount', e.target.value)}
                  placeholder="e.g. 10000000"
                />
                <FormHelperText>
                  {form.commitmentAmount
                    ? formatCurrency(parseFloat(form.commitmentAmount))
                    : 'USD'}
                </FormHelperText>
              </div>
              <div>
                <FormLabel htmlFor="ca-called">Called Capital (%)</FormLabel>
                <Input
                  id="ca-called"
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={form.calledCapitalPercent}
                  onChange={(e) => updateField('calledCapitalPercent', e.target.value)}
                  placeholder="e.g. 65"
                />
                <FormHelperText>0-100</FormHelperText>
              </div>
            </div>

            <div>
              <FormLabel htmlFor="ca-nav">Current Fund NAV</FormLabel>
              <Input
                id="ca-nav"
                type="number"
                min={0}
                step={100000}
                value={form.currentFundNav}
                onChange={(e) => updateField('currentFundNav', e.target.value)}
                placeholder="e.g. 850000000"
              />
              <FormHelperText>
                {form.currentFundNav
                  ? formatCurrency(parseFloat(form.currentFundNav))
                  : 'Net asset value of the fund'}
              </FormHelperText>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FormLabel>Fund Strategy</FormLabel>
                <Select
                  options={FUND_STRATEGY_OPTIONS}
                  value={form.fundStrategy}
                  onChange={(e) => updateField('fundStrategy', e.target.value)}
                  placeholder="Select strategy"
                />
              </div>
              <div>
                <FormLabel>GP Tier</FormLabel>
                <Select
                  options={GP_TIER_OPTIONS}
                  value={form.gpTier}
                  onChange={(e) => updateField('gpTier', e.target.value)}
                  placeholder="Select quartile"
                />
              </div>
            </div>

            {/* Fund Terms */}
            <div className="border-t border-recessed pt-4 mt-4">
              <Heading level={3} as={3} size="label" className="mb-3 text-sm">Fund Terms</Heading>
              <div className="space-y-3">
                <Switch
                  isSelected={form.gpConsentRequired}
                  onChange={(val: boolean) => updateField('gpConsentRequired', val)}
                  label="GP Consent Required"
                />
                <Switch
                  isSelected={form.rofrApplies}
                  onChange={(val: boolean) => updateField('rofrApplies', val)}
                  label="ROFR Applies"
                />
                <Switch
                  isSelected={form.pledgePermitted}
                  onChange={(val: boolean) => updateField('pledgePermitted', val)}
                  label="Pledge Permitted"
                />
                <div>
                  <FormLabel>Distribution Frequency</FormLabel>
                  <Select
                    options={DISTRIBUTION_FREQUENCY_OPTIONS}
                    value={form.distributionFrequency}
                    onChange={(e) => updateField('distributionFrequency', e.target.value)}
                    placeholder="Select frequency"
                  />
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Button
                variant="primary"
                onClick={handleAssessment}
                disabled={!isFormValid || isLoading}
                className="w-full"
              >
                {isLoading ? 'Assessing...' : 'Run Collateral Assessment'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Assessment Results */}
        <div className="space-y-4">
          {isLoading && (
            <Card>
              <BodyText className="text-secondary text-center py-8">
                Computing collateral assessment...
              </BodyText>
            </Card>
          )}

          {error && (
            <Card>
              <div className="p-4 bg-error/10 border border-error/20 rounded">
                <BodyText className="text-error font-semibold">Assessment Error</BodyText>
                <BodyText className="text-error mt-1">{error}</BodyText>
              </div>
            </Card>
          )}

          {assessment && !isLoading && (
            <>
              {/* Key Metrics */}
              <Card>
                <Heading level={3} as={3} size="card" className="mb-4">Collateral Assessment</Heading>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-3 border-b border-surface">
                    <BodyText className="text-secondary">Estimated Collateral Value</BodyText>
                    <MetricDisplay
                      value={formatCurrency(assessment.estimatedCollateralValue)}
                      className="text-primary"
                    />
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-surface">
                    <BodyText className="text-secondary">Recommended LTV</BodyText>
                    <MetricDisplay
                      value={formatPercent(assessment.recommendedLtv)}
                      className="text-primary font-mono"
                    />
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-surface">
                    <BodyText className="text-secondary">Max LTV</BodyText>
                    <MetricDisplay
                      value={formatPercent(assessment.maxLtv)}
                      className="text-primary font-mono"
                    />
                  </div>
                  <div className="flex justify-between items-center py-3 bg-accent/5 px-4 -mx-4 rounded">
                    <BodyText className="font-semibold text-accent">Max Loan Amount</BodyText>
                    <MetricDisplay
                      value={formatCurrency(assessment.maxLoanAmount ?? 0)}
                      className="text-2xl text-accent font-semibold"
                    />
                  </div>
                </div>

                {/* Risk Rating */}
                <div className="mt-4 flex items-center gap-3">
                  <BodyText className="text-secondary">Risk Rating:</BodyText>
                  <span
                    className={`inline-block px-3 py-1 rounded border text-sm font-semibold capitalize ${
                      RISK_RATING_STYLES[assessment.riskRating as string] || ''
                    }`}
                  >
                    {assessment.riskRating}
                  </span>
                  <BodyText className="text-tertiary text-sm">
                    Score: {assessment.riskScore ?? 0}/100
                  </BodyText>
                </div>
              </Card>

              {/* CHA Tier Thresholds */}
              <Card>
                <Heading level={3} as={3} size="card" className="mb-4">CHA Tier Thresholds</Heading>
                <TableScroll label="CHA tier thresholds">
                  <table className="w-full text-sm font-sans">
                    <thead>
                      <tr className="border-b border-recessed">
                        <th className="text-left py-2 text-secondary font-medium">Threshold</th>
                        <th className="text-right py-2 text-secondary font-medium">LTV</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-surface">
                        <td className="py-2 text-primary">Warning</td>
                        <td className="py-2 text-right font-mono text-primary">
                          {formatPercent(assessment.marginCallThresholds?.warningLtv ?? 0)}
                        </td>
                      </tr>
                      <tr className="border-b border-surface">
                        <td className="py-2 text-primary">CHA tier change</td>
                        <td className="py-2 text-right font-mono text-warning">
                          {formatPercent(assessment.marginCallThresholds?.marginCallLtv ?? 0)}
                        </td>
                      </tr>
                      <tr className="border-b border-surface">
                        <td className="py-2 text-primary">Forced Liquidation</td>
                        <td className="py-2 text-right font-mono text-error">
                          {formatPercent(assessment.marginCallThresholds?.forcedLiquidationLtv ?? 0)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 text-primary">Cure Period</td>
                        <td className="py-2 text-right font-mono text-primary">
                          {assessment.marginCallThresholds?.curePeriodDays} days
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </TableScroll>
              </Card>

              {/* Liquidation Parameters */}
              <Card>
                <Heading level={3} as={3} size="card" className="mb-4">Liquidation Parameters</Heading>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b border-surface">
                    <BodyText className="text-secondary">GP Consent Period</BodyText>
                    <BodyText className="font-mono text-primary">
                      {assessment.liquidationParameters?.gpConsentTimeDays} days
                    </BodyText>
                  </div>
                  <div className="flex justify-between py-2 border-b border-surface">
                    <BodyText className="text-secondary">Total Liquidation Time</BodyText>
                    <BodyText className="font-mono text-primary">
                      {assessment.liquidationParameters?.totalLiquidationTimeDays} days
                    </BodyText>
                  </div>
                  <div className="flex justify-between py-2">
                    <BodyText className="text-secondary">Estimated Recovery Rate</BodyText>
                    <BodyText className="font-mono text-primary">
                      {formatPercent(assessment.liquidationParameters?.estimatedRecoveryRate ?? 0)}
                    </BodyText>
                  </div>
                </div>
              </Card>

              {/* Risk Factors */}
              {assessment.riskFactors && assessment.riskFactors.length > 0 && (
                <Card>
                  <Heading level={3} as={3} size="card" className="mb-4">Risk Factors</Heading>
                  <TableScroll label="Risk factors">
                    <table className="w-full text-sm font-sans">
                      <thead>
                        <tr className="border-b border-recessed">
                          <th className="text-left py-2 text-secondary font-medium">Factor</th>
                          <th className="text-left py-2 text-secondary font-medium">Severity</th>
                          <th className="text-left py-2 text-secondary font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assessment.riskFactors.map((rf, idx) => (
                          <tr key={idx} className="border-b border-surface">
                            <td className="py-2 text-primary">{rf.factor}</td>
                            <td className="py-2">
                              <Badge variant={SEVERITY_BADGE_VARIANT[rf.severity as string] || 'neutral'}>
                                {rf.severity}
                              </Badge>
                            </td>
                            <td className="py-2 text-tertiary">{rf.impact}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                </Card>
              )}

              {/* Mitigants */}
              {assessment.mitigants && assessment.mitigants.length > 0 && (
                <Card>
                  <Heading level={3} as={3} size="card" className="mb-4">Mitigants</Heading>
                  <ul className="space-y-2">
                    {assessment.mitigants.map((m, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-growth mt-0.5 font-semibold">--</span>
                        <BodyText className="text-primary">{m.mitigant}</BodyText>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* Advance Rate Guidance */}
              {assessment.advanceRateGuidance && (
                <Card>
                  <Heading level={3} as={3} size="card" className="mb-2">Advance Rate Guidance</Heading>
                  <BodyText className="text-primary">
                    {assessment.advanceRateGuidance.fundTypeGuidance}
                    {assessment.advanceRateGuidance.typicalAdvanceRateLow != null &&
                      assessment.advanceRateGuidance.typicalAdvanceRateHigh != null &&
                      ` (${formatPercent(assessment.advanceRateGuidance.typicalAdvanceRateLow)} - ${formatPercent(assessment.advanceRateGuidance.typicalAdvanceRateHigh)})`}
                  </BodyText>
                  {assessment.advanceRateGuidance.notes && (
                    <BodyText className="text-tertiary text-sm mt-1">
                      {assessment.advanceRateGuidance.notes}
                    </BodyText>
                  )}
                </Card>
              )}
            </>
          )}

          {!assessment && !isLoading && !error && (
            <Card>
              <BodyText className="text-secondary text-center py-8">
                Enter fund and collateral parameters to begin assessment
              </BodyText>
            </Card>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Step 2: Stress Test                                                 */}
      {/* ------------------------------------------------------------------ */}
      {assessment && (
        <div className="mt-6">
          <Card>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <Heading level={3} as={3} size="card">Stress Test Scenarios</Heading>
              <Button
                variant="secondary"
                onClick={handleStressTest}
                disabled={isStressLoading}
              >
                {isStressLoading ? 'Running...' : 'Run Stress Tests'}
              </Button>
            </div>
            <BodyText className="text-secondary mb-4">
              Evaluate collateral resilience under moderate (25% decline), severe (45% decline),
              and extreme (60% decline) NAV scenarios.
            </BodyText>

            {stressError && (
              <div className="p-4 bg-error/10 border border-error/20 rounded mb-4">
                <BodyText className="text-error font-semibold">Stress Test Error</BodyText>
                <BodyText className="text-error mt-1">{stressError}</BodyText>
              </div>
            )}

            {isStressLoading && (
              <BodyText className="text-secondary text-center py-6">
                Executing stress scenarios...
              </BodyText>
            )}

            {stressResults && !isStressLoading && (
              <TableScroll label="Stress test scenarios">
                <table className="w-full text-sm font-sans">
                  <thead>
                    <tr className="border-b border-recessed">
                      <th className="text-left py-2 text-secondary font-medium">Scenario</th>
                      <th className="text-right py-2 text-secondary font-medium">Stressed NAV</th>
                      <th className="text-right py-2 text-secondary font-medium">Stressed LTV</th>
                      <th className="text-center py-2 text-secondary font-medium">CHA tier change</th>
                      <th className="text-right py-2 text-secondary font-medium">Loss Estimate</th>
                      <th className="text-right py-2 text-secondary font-medium">Coverage Ratio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stressResults.map((scenario, idx) => (
                      <tr key={idx} className="border-b border-surface">
                        <td className="py-2 text-primary font-medium">
                          {scenario.scenarioName}
                        </td>
                        <td className="py-2 text-right font-mono text-primary">
                          {formatCurrency(scenario.stressedNav)}
                        </td>
                        <td className="py-2 text-right font-mono text-primary">
                          {formatPercent(scenario.stressedLtv)}
                        </td>
                        <td className="py-2 text-center">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                              scenario.marginCallTriggered
                                ? 'bg-error/10 text-error'
                                : 'bg-growth/10 text-growth'
                            }`}
                          >
                            {scenario.marginCallTriggered ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="py-2 text-right font-mono text-primary">
                          {formatCurrency(scenario.estimatedLoss ?? 0)}
                        </td>
                        <td className="py-2 text-right font-mono text-primary">
                          {(scenario.coverageRatio ?? 0).toFixed(2)}x
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}

            {!stressResults && !isStressLoading && !stressError && (
              <BodyText className="text-tertiary text-center py-4">
                Press "Run Stress Tests" to evaluate decline scenarios
              </BodyText>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
